<script lang="ts">
    import { useFragment } from '$gql/fragment-masking.js';
    import {
        type FeedParticipantFragment,
        FeedParticipantFragmentDoc,
        ParticipantAvatarFragmentDoc,
        ParticipantCardFragmentDoc,
        type ViewMetaFragment,
    } from '$gql/graphql.js';
    import ParticipantAvatar from '../ParticipantAvatar/ParticipantAvatar.svelte';
    import ParticipantCard from '$components/ParticipantCard/ParticipantCard.svelte';
    import * as t from '$trans';
    import { getContainer } from '$components/Context/ContainerContext';

    const { lang } = getContainer();

    function calcAuthor(
        viewMeta: ViewMetaFragment | null,
    ): FeedParticipantFragment | null {
        const firstCommentAuthor = viewMeta?.commentAuthors?.[0] ?? null;
        const firstReactionAuthor = viewMeta?.reactionAuthors?.[0] ?? null;
        const firstShareAuthor = viewMeta?.shareAuthors?.[0] ?? null;

        if (firstShareAuthor != null) {
            return useFragment(FeedParticipantFragmentDoc, firstShareAuthor);
        } else if (firstCommentAuthor != null) {
            return useFragment(FeedParticipantFragmentDoc, firstCommentAuthor);
        } else if (firstReactionAuthor != null) {
            return useFragment(FeedParticipantFragmentDoc, firstReactionAuthor);
        }

        return null;
    }

    function calcType({
        nbCommentAuthors,
        nbReactionAuthors,
        nbShareAuthors,
    }: {
        nbCommentAuthors: number;
        nbReactionAuthors: number;
        nbShareAuthors: number;
    }): 'comment' | 'reaction' | 'share' | null {
        if (nbShareAuthors > 0) {
            return 'share';
        } else if (nbCommentAuthors > 0) {
            return 'comment';
        } else if (nbReactionAuthors > 0) {
            return 'reaction';
        }

        return null;
    }

    export let viewMeta: ViewMetaFragment;
    $: nbCommentAuthors = viewMeta?.commentAuthors?.length ?? 0;
    $: nbReactionAuthors = viewMeta?.reactionAuthors?.length ?? 0;
    $: nbShareAuthors = viewMeta?.shareAuthors?.length ?? 0;
    $: author = calcAuthor(viewMeta);
    $: type = calcType({ nbCommentAuthors, nbReactionAuthors, nbShareAuthors });
</script>

{#if viewMeta && (viewMeta?.commentAuthors?.length > 0 || viewMeta?.reactionAuthors?.length > 0 || viewMeta?.shareAuthors?.length > 0)}
    <div class={'text-sm pb-2 mb-4 border-solid border-0 border-b border-gray-300'}>
        <span class={'inline-flex items-center'}>
            {#if author != null}
                <ParticipantCard
                    participant={useFragment(ParticipantCardFragmentDoc, author)}
                >
                    <a
                        class={'font-semibold hover:underline text-link hover:text-link-hover inline-flex items-center'}
                        href={author?.participantProfileLink}
                        data-sveltekit-reload
                        target={'_blank'}
                    >
                        <ParticipantAvatar
                            participant={useFragment(
                                ParticipantAvatarFragmentDoc,
                                author,
                            )}
                            size={'xs'}
                            dontWrapInLink
                            imgClass={'mr-2'}
                        />{' '}
                        {author?.participantName}
                    </a>
                </ParticipantCard>
            {/if}
            <span class={'ml-2 text-gray-500'}>
                {' '}
                {type === 'share'
                    ? t.feed_post.shared({}, $lang)
                    : type === 'comment'
                      ? t.feed_post.commented({}, $lang)
                      : t.feed_post.reacted({}, $lang)}
            </span>
        </span>
    </div>
{/if}
